package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.CanvasAst
import top.wcpe.allincore.dsl.compiler.ast.DrawAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析 `canvas "<id>" { width height duration loopMode draw {} once {} ... }` 块
 * （M11.6 引入，M11.7 Stage 1 把 width/height/duration 升级为任意表达式）。
 *
 * 必填字段：[CanvasAst.width]、[CanvasAst.height]、[CanvasAst.drawBlock]。
 *
 * 可选字段：
 *  - `duration = <expr>`：单周期时长（毫秒），默认由 Emitter 填入 1000。
 *  - `loopMode = "<string>"`：循环模式，默认由 Emitter 填入 "true"。
 *  - `once { ... }`：首帧一次性指令。
 *  - 其它 `name = expr` 视作 canvas 元素自身 prop（与 box prop 同语义）。
 *
 * width/height/duration 接受任意表达式（含 props.x 引用）。负数、非整数等
 * 校验延后到 Emitter / Runtime 阶段，按字面量与表达式两种路径分别处理。
 */
internal class CanvasBlockParser(private val p: Parser) {

    /** 入口：从 `canvas` 关键字开始解析整个 canvas 元素块。 */
    fun parse(): CanvasAst {
        val canvasTok = p.expect(TokenType.KW_CANVAS)
        val idTok = p.expect(TokenType.STRING, "canvas 后必须跟字符串 id")
        p.expect(TokenType.LBRACE, "canvas '${idTok.literalValue}' 头部后缺少 '{'")
        p.skipNewlines()

        var width: ExprAst? = null
        var height: ExprAst? = null
        var duration: ExprAst? = null
        var loopMode: String? = null
        val attrs = mutableListOf<ElementAst.PropAssign>()
        var drawBlock: DrawAst? = null
        var onceBlock: DrawAst? = null

        val drawParser = DrawBlockParser(p)

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val head = p.current()
            when {
                head.type == TokenType.KW_DRAW -> {
                    if (drawBlock != null) {
                        p.fail("canvas '${idTok.literalValue}' 的 draw 块只能声明一次", head.position)
                    }
                    drawBlock = drawParser.parseBlock(TokenType.KW_DRAW)
                }
                head.type == TokenType.KW_ONCE -> {
                    if (onceBlock != null) {
                        p.fail("canvas '${idTok.literalValue}' 的 once 块只能声明一次", head.position)
                    }
                    onceBlock = drawParser.parseBlock(TokenType.KW_ONCE)
                }
                isPropNameStart(head) && p.peek(1).type == TokenType.EQUALS -> {
                    val nameTok = p.advance()
                    p.advance() // '='
                    val name = nameTok.lexeme
                    when (name) {
                        "width" -> {
                            if (width != null) p.fail("canvas width 重复声明", nameTok.position)
                            width = ExpressionParser(p).parse()
                        }
                        "height" -> {
                            if (height != null) p.fail("canvas height 重复声明", nameTok.position)
                            height = ExpressionParser(p).parse()
                        }
                        "duration" -> {
                            if (duration != null) p.fail("canvas duration 重复声明", nameTok.position)
                            duration = ExpressionParser(p).parse()
                        }
                        "loopMode" -> {
                            if (loopMode != null) p.fail("canvas loopMode 重复声明", nameTok.position)
                            val valueTok = p.expect(TokenType.STRING, "canvas loopMode 必须是字符串")
                            loopMode = valueTok.literalValue as String
                        }
                        else -> {
                            attrs += ElementAst.PropAssign(
                                name = name,
                                value = ExpressionParser(p).parse(),
                                position = nameTok.position,
                            )
                        }
                    }
                }
                else -> {
                    p.fail("canvas 块内不识别的成员 '${head.lexeme}'", head.position)
                }
            }

            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "canvas 成员后缺少换行")
                p.skipNewlines()
            }
        }

        val close = p.expect(TokenType.RBRACE, "canvas '${idTok.literalValue}' 块缺少 '}'")

        val finalWidth = width ?: p.fail("canvas '${idTok.literalValue}' 缺少必填字段 width", canvasTok.position)
        val finalHeight = height ?: p.fail("canvas '${idTok.literalValue}' 缺少必填字段 height", canvasTok.position)
        val finalDraw = drawBlock ?: p.fail("canvas '${idTok.literalValue}' 缺少必填 draw 块", canvasTok.position)

        return CanvasAst(
            id = idTok.literalValue as String,
            width = finalWidth,
            height = finalHeight,
            durationMs = duration,
            loopMode = loopMode,
            attrs = attrs,
            drawBlock = finalDraw,
            onceBlock = onceBlock,
            position = p.completePosition(canvasTok.position, close),
        )
    }

    /** canvas 块内可作为 prop 名开头的 token 集合。 */
    private fun isPropNameStart(token: Token): Boolean =
        token.type == TokenType.IDENTIFIER ||
            token.type == TokenType.KW_COLOR ||
            token.type == TokenType.KW_STRING ||
            token.type == TokenType.KW_NUMBER ||
            token.type == TokenType.KW_BOOL
}

